import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { MaskedLines, FadeUp, Overline, GoldRule } from "../components/Reveal";

export const RICH_LINK_CLASS =
  "text-navy font-medium underline decoration-gold underline-offset-4 hover:text-gold transition-colors duration-300";

export const renderRichText = (text, links = []) => {
  if (!links || links.length === 0) return text;
  let parts = [text];
  links.forEach((link) => {
    const next = [];
    parts.forEach((part) => {
      if (typeof part !== "string") {
        next.push(part);
        return;
      }
      const idx = part.indexOf(link.match);
      if (idx === -1) {
        next.push(part);
        return;
      }
      next.push(part.slice(0, idx));
      next.push(link);
      next.push(part.slice(idx + link.match.length));
    });
    parts = next;
  });
  return parts.map((part, i) => {
    if (typeof part === "string") return part;
    const isInternal = !part.external && part.href.startsWith("/");
    if (isInternal) {
      return (
        <Link key={i} to={part.href} className={RICH_LINK_CLASS} data-testid={part.testId}>
          {part.match}
        </Link>
      );
    }
    const isHttp = part.href.startsWith("http");
    return (
      <a
        key={i}
        href={part.href}
        className={RICH_LINK_CLASS}
        data-testid={part.testId}
        {...(isHttp ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {part.match}
      </a>
    );
  });
};

const Block = ({ block, pageSlug, index }) => {
  const testId = `${pageSlug}-block-${index}`;
  switch (block.type) {
    case "meta":
      return (
        <p className="text-xs tracking-[0.18em] uppercase text-navy/50 font-medium" data-testid={testId}>
          {block.text}
        </p>
      );
    case "h2":
      return (
        <h2
          className="mt-14 font-display text-2xl sm:text-3xl text-navy tracking-tight"
          data-testid={testId}
        >
          {block.text}
        </h2>
      );
    case "p":
      return (
        <p
          className="mt-5 text-base sm:text-lg text-navy/75 leading-relaxed font-light"
          data-testid={testId}
        >
          {renderRichText(block.text, block.links)}
        </p>
      );
    case "lines":
      return (
        <div className="mt-5 space-y-1.5" data-testid={testId}>
          {block.lines.map((line, i) =>
            typeof line === "string" ? (
              <p key={i} className="text-base sm:text-lg text-navy/75 leading-relaxed font-light">
                {line}
              </p>
            ) : (
              <p key={i} className="text-base sm:text-lg text-navy/75 leading-relaxed font-light">
                {renderRichText(line.text, line.links)}
              </p>
            )
          )}
        </div>
      );
    case "cta":
      return (
        <a
          href={block.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={block.testId}
          className="group mt-8 inline-flex items-center gap-3 bg-navy text-white text-sm tracking-[0.18em] uppercase font-medium px-9 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
        >
          {block.label}
          <ArrowUpRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
      );
    default:
      return null;
  }
};

export const ContentPage = ({ page }) => (
  <div data-testid={`page-${page.slug}`}>
    <SEO title={page.seoTitle || `${page.title} | Lunar Ambitions`} description={page.seoDescription} />
    <section className="relative overflow-hidden bg-white pt-52 pb-24 lg:pt-72 sm:pb-32">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Overline>Lunar Ambitions · Orlando, FL</Overline>
        <h1
          className="mt-6 font-display font-normal tracking-tight leading-[1.08] text-navy text-4xl sm:text-5xl lg:text-6xl"
          data-testid={`${page.slug}-heading`}
        >
          <MaskedLines lines={[page.title]} />
        </h1>
        <FadeUp delay={0.35}>
          <GoldRule className="mt-10 w-24" />
          <div className="mt-6">
            {page.blocks.map((block, i) => (
              <Block key={i} block={block} pageSlug={page.slug} index={i} />
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  </div>
);
